@if (isLoading) {
  <div class="grid grid-cols-1 gap-4">
    @for (i of [1, 2]; track i) {
      <vw-skeleton-loader [width]="'100%'" [height]="'88px'" [borderRadius]="'1rem'"></vw-skeleton-loader>
    }
  </div>
} @else {
  <div class="flex flex-col gap-5">
    @for (z of zones; track z.zone.uid) {
      <section class="flex flex-col gap-3">
        @if (z.beds.length || z.looseCultures.length) {
          <!-- En-tête de zone + petit « + » (ajouter une planche) -->
          <div class="flex items-center justify-between gap-2">
            <div class="flex min-w-0 items-center gap-2 cursor-pointer" (click)="openZoneEdit(z)">
              <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#DAF2E0] text-base">{{ zoneEmoji(z.zone.locationType) }}</span>
              <div class="min-w-0">
                <h3 class="truncate text-lg font-semibold leading-tight text-[#023E42]">{{ z.zone | vwZoneName }}</h3>
                <p class="truncate text-[11px] leading-tight text-[#9DA4A2]">
                  @if (z.zone | vwHasCustomZoneName) {
                    {{ 'profile.addresses.edit-address.edit-locations.locations.' + z.zone.locationType | translate }} ·
                  }
                  @if (z.zone.sunchineLevels) {
                    {{ 'garden.beds.enums.exposure.' + z.zone.sunchineLevels | translate }} ·
                  }
                  {{ 'garden.overview.beds-count' | translate: { count: z.beds.length } }}
                </p>
              </div>
            </div>
            <button type="button" (click)="addBedToZone(z.zone)"
              class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-lg leading-none text-[#6B7472] active:bg-primary-50 active:text-primary-600"
              [attr.aria-label]="('garden.overview.add-bed.title' | translate) + ' — ' + z.zone.name">+</button>
          </div>

          <!-- Planches en lignes compactes -->
          @if (z.beds.length) {
            <div class="divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-lg">
              @for (b of z.beds; track b.bed.uid) {
                <div (click)="openBed(b.bed)" class="cursor-pointer active:bg-primary-50">
                  <vw-bed-item [bed]="b.bed" [cultures]="b.cultures" [count]="b.count" [plantCount]="b.plantCount" [freeCells]="b.freeCells" [occupied]="b.occupied" [actionNeeded]="b.actionNeeded"></vw-bed-item>
                </div>
              }
            </div>
          }

          <!-- Cultures de la zone non placées dans une planche (rattachées à la zone) -->
          @if (z.looseCultures.length) {
            <div class="rounded-2xl bg-white p-3 shadow-lg">
              <p class="mb-2 text-[11px] font-medium uppercase tracking-wide text-[#9DA4A2]">🌱 {{ 'garden.overview.off-bed' | translate }}</p>
              <div class="grid grid-cols-3 gap-3">
                @for (g of z.looseCultures; track $index) {
                  <div (click)="openCulture(g)" class="cursor-pointer">
                    <div class="relative aspect-square w-full overflow-hidden rounded-lg">
                      <vw-cultivation-item-viewer [cultivation]="$any(g.culture)" displayType="SMALL" [showName]="false" class="block h-full w-full"></vw-cultivation-item-viewer>
                      @if (g.actionNeeded) {
                        <vw-action-badge class="absolute left-1 top-1" [ariaLabel]="'garden.overview.action-todo' | translate"></vw-action-badge>
                      }
                      @if (g.count > 1) {
                        <span class="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-white/95 px-1.5 py-0.5 text-[11px] font-bold leading-none text-[#023E42] shadow-sm">
                          <img src="/assets/svg/leaf.svg" alt="" class="h-3.5 w-3.5" />{{ g.count }}
                        </span>
                      }
                    </div>
                    <p class="mt-1.5 truncate text-center text-sm font-semibold text-[#023E42]">{{ $any(g.culture)?.name }}</p>
                  </div>
                }
              </div>
            </div>
          }
        } @else {
          <!-- Zone vide : une seule ligne -->
          <div class="flex items-center justify-between gap-2 rounded-2xl bg-white px-3 py-2.5 shadow-lg">
            <div class="flex min-w-0 items-center gap-2 cursor-pointer" (click)="openZoneEdit(z)">
              <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#DAF2E0] text-base">{{ zoneEmoji(z.zone.locationType) }}</span>
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold leading-tight text-[#023E42]">{{ z.zone | vwZoneName }}</p>
                <p class="truncate text-[11px] leading-tight text-[#9DA4A2]">
                  @if (z.zone | vwHasCustomZoneName) {
                    {{ 'profile.addresses.edit-address.edit-locations.locations.' + z.zone.locationType | translate }} ·
                  }
                  {{ 'garden.overview.beds-count' | translate: { count: 0 } }}
                </p>
              </div>
            </div>
            <button type="button" (click)="addBedToZone(z.zone)"
              class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-lg leading-none text-[#6B7472] active:bg-primary-50 active:text-primary-600"
              [attr.aria-label]="('garden.overview.add-bed.title' | translate) + ' — ' + z.zone.name">+</button>
          </div>
        }
      </section>
    }

    <!-- Cultures sans zone (orphelines) uniquement — celles rattachées à une zone restent sous leur zone. -->
    @if (orphanCultures.length) {
      <section class="flex flex-col gap-2 border-t border-gray-100 pt-4">
        <p class="text-xs font-semibold uppercase tracking-wide text-[#9DA4A2]">{{ 'garden.overview.no-zone' | translate }}</p>
        <div class="grid grid-cols-3 gap-3">
          @for (g of orphanCultures; track $index) {
            <div (click)="openCulture(g)" class="cursor-pointer">
              <div class="relative aspect-square w-full overflow-hidden rounded-lg">
                <vw-cultivation-item-viewer [cultivation]="$any(g.culture)" displayType="SMALL" [showName]="false" class="block h-full w-full"></vw-cultivation-item-viewer>
                @if (g.actionNeeded) {
                  <vw-action-badge class="absolute left-1 top-1" [ariaLabel]="'garden.overview.action-todo' | translate"></vw-action-badge>
                }
                @if (g.count > 1) {
                  <span class="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-white/95 px-1.5 py-0.5 text-[11px] font-bold leading-none text-[#023E42] shadow-sm">
                    <img src="/assets/svg/leaf.svg" alt="" class="h-3.5 w-3.5" />{{ g.count }}
                  </span>
                }
              </div>
              <p class="mt-1.5 truncate text-center text-sm font-semibold text-[#023E42]">{{ $any(g.culture)?.name }}</p>
            </div>
          }
        </div>
      </section>
    }

    @if (!zones.length && !orphanCultures.length) {
      <p class="py-8 text-center text-sm text-[#9DA4A2]">{{ 'garden.overview.empty' | translate }}</p>
    }
  </div>
}

<!-- Panneau « Ajouter une zone » : wizard 2 étapes (la zone → ses planches), slide-in. -->
<div
  class="fixed inset-0 z-[1000] overflow-y-auto bg-white transition-transform duration-200 ease-in-out"
  [ngClass]="showZoneForm ? 'translate-x-0' : 'pointer-events-none translate-x-full'"
>
  @if (showZoneForm) {
    <vw-zone-create-wizard (finished)="onZoneFormClosed($event)"></vw-zone-create-wizard>
  }
</div>

<!-- Édition d'une zone (tap sur l'en-tête) : formulaire existant + action « Supprimer la zone ». -->
<div
  class="fixed inset-0 z-[1000] overflow-y-auto bg-white transition-transform duration-200 ease-in-out"
  [ngClass]="showZoneEdit ? 'translate-x-0' : 'pointer-events-none translate-x-full'"
>
  @if (showZoneEdit) {
    <div class="p-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <vw-user-adress-location-form
        [location]="editZone"
        [showDelete]="true"
        (closeLocationForm)="onZoneEditClosed($event)"
        (deleteLocation)="onZoneDeleteRequest()"
      ></vw-user-adress-location-form>
    </div>
  }
</div>

<!-- Confirmation de suppression de zone (archive ses cultures, conserve l'historique). -->
<vw-confirm-dialog
  [isOpen]="showZoneDeleteConfirm"
  [title]="'garden.overview.delete-zone.title' | translate"
  [message]="'garden.overview.delete-zone.message' | translate: { count: zoneDeleteCount }"
  [confirmLabel]="'garden.overview.delete-zone.confirm' | translate"
  [loading]="deletingZone"
  (confirmed)="confirmZoneDelete()"
  (cancelled)="cancelZoneDelete()"
></vw-confirm-dialog>
